import { computed, ref, toValue, watch } from "vue";
import { getSquareVideoList } from "~/api/api";

const extractShortsList = res => {
	const payload = res?.data && typeof res.data === "object" ? res.data : res || {};
	return Array.isArray(payload.list) ? payload.list : [];
};

export function useShortsNavigation(getCurrentId) {
	const list = ref([]);
	const currentPage = ref(0);
	const finished = ref(false);
	const locating = ref(false);

	const getId = () => {
		const id = toValue(getCurrentId);
		return id != null ? String(id) : "";
	};

	const currentIndex = computed(() => list.value.findIndex(item => String(item.id) === getId()));

	const hasPrev = computed(() => currentIndex.value > 0);

	const hasNext = computed(
		() =>
			(currentIndex.value >= 0 && currentIndex.value < list.value.length - 1) ||
			(currentIndex.value >= 0 && !finished.value)
	);

	const fetchPage = async (page, append = true) => {
		const res = await getSquareVideoList({
			video_screen_mode: 2,
			page,
		});
		const items = extractShortsList(res);

		if (append) {
			if (items.length) {
				const existingIds = new Set(list.value.map(item => String(item.id)));
				const merged = items.filter(item => !existingIds.has(String(item.id)));
				if (merged.length) list.value = [...list.value, ...merged];
			}
		} else {
			list.value = items;
		}

		currentPage.value = page;
		if (!items.length) finished.value = true;
		return items.length;
	};

	const ensureCurrentLocated = async () => {
		const currentId = getId();
		if (!currentId || locating.value) return;

		if (currentIndex.value !== -1) return;

		locating.value = true;
		try {
			if (!list.value.length) {
				await fetchPage(1, false);
			}

			while (currentIndex.value === -1 && !finished.value) {
				const count = await fetchPage(currentPage.value + 1, true);
				if (!count) break;
			}
		} catch (error) {
			console.error("定位 Shorts 列表失败:", error);
		} finally {
			locating.value = false;
		}
	};

	const getAdjacentId = async direction => {
		await ensureCurrentLocated();

		const index = currentIndex.value;
		if (index === -1) return null;

		const targetIndex = direction === "prev" ? index - 1 : index + 1;

		if (targetIndex >= 0 && targetIndex < list.value.length) {
			return list.value[targetIndex]?.id ?? null;
		}

		if (direction === "next" && !finished.value) {
			await fetchPage(currentPage.value + 1, true);
			return list.value[targetIndex]?.id ?? null;
		}

		return null;
	};

	watch(
		getCurrentId,
		() => {
			if (currentIndex.value === -1) {
				ensureCurrentLocated();
			}
		},
		{ immediate: true }
	);

	return {
		hasPrev,
		hasNext,
		locating,
		getAdjacentId,
	};
}
